import { useNavigator } from '@repo/toolkit/use-navigator'
import { SettingIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getCanGoOutAloneCondition } from '../../../api/can-go-out-alone-conditions/get-can-go-out-alone-condition'
import { updateCanGoOutAloneCondition } from '../../../api/can-go-out-alone-conditions/update-can-go-out-alone-condition'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import { CanGoOutAloneConditionForm } from '../../../components/pages/can-go-out-alone-condition/can-go-out-alone-condition-form'
import { Paths } from '../../../lib/constants/path'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapCanGoOutAloneConditionIntoInput } from './utils'

export function CanGoOutAloneConditionsUpdatePage() {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  if (!id || isNaN(+id))
    return <ItemNotFound title="Identifiant de condition de sortie seul invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateCanGoOutAloneCondition,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['can-go-out-alone-conditions', id] })
      toast.success('Condition de sortie seul modifiée avec succès')
    }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['can-go-out-alone-conditions', id],
    queryFn: () => getCanGoOutAloneCondition(+id)
  })

  if (isLoading) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={'Modifier une condition de sortie seul'} icon={SettingIcon} />
      <div className={'h-4'} />
      <CanGoOutAloneConditionForm
        defaultValues={mapCanGoOutAloneConditionIntoInput(data)}
        buttonsConfig={updateButtonsConfig({
          onCancel: () => back(Paths.CAN_GO_OUT_ALONE_CONDITION_LIST_PAGE),
          onUpdate: (values) => mutateAsync({ id: data.id, ...values }),
          onUpdateThenCreate: async (values) => {
            await mutateAsync({ id: data.id, ...values })
            navigate(Paths.CAN_GO_OUT_ALONE_CONDITION_CREATE_PAGE)
          },
          onUpdateThenList: async (values) => {
            await mutateAsync({ id: data.id, ...values })
            navigate(Paths.CAN_GO_OUT_ALONE_CONDITION_LIST_PAGE, { replace: true })
          }
        })}
      />
    </div>
  )
}
